import { Box, IconButton, TextField } from "@mui/material";
import { useRef } from "react";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import EditIcon from "@mui/icons-material/Edit";
import {
    CoverBooleanTypeList,
    StoreOpeningConditionsBooleanTypeList,
    StoreOpeningConditionsTypeList,
    DrawingBooleanTypeList,
    DrawingTypeList,
} from "../../data/type";
import { setPermissionFunc } from "../components/PermissionFunc";
import { useAppSelector } from "../../app/hooks";
import {
    chatBubbleIconSx,
    editIconCircleSx,
    iconHover,
} from "./ChatBubbleAndEditIconStyle";
import {
    BGDefaultColor,
    BGChangeColor,
    BGDisabledColor,
    BGErrorColor,
    TextDefaultColor,
    TextChangeColor,
    TextDisabledColor,
    BorderDefaultColor,
    BorderHoverColor,
    BorderFocusedColor,
    BorderErrorColor
} from "../../data/table";
import { textColor } from "../../data/tableFunc";
import { LOCAL_CONSTANT } from "../Const";
import {
    setTablePhysicsName,
    pageToTableMapFunc,
    isIpad
} from "../Common";

export interface TextFieldProps {
    props: {
        userType: string;
        disabled: boolean;
        pageName: string;
        labelName: string;
        rows: number;
        width: string;
        InputValueObj: StoreOpeningConditionsTypeList | DrawingTypeList | any;
        ReadOnlyObj:
            | StoreOpeningConditionsBooleanTypeList
            | DrawingBooleanTypeList
            | any;
        setReadOnlyObj:
            | React.Dispatch<React.SetStateAction<CoverBooleanTypeList>>
            | React.Dispatch<React.SetStateAction<StoreOpeningConditionsBooleanTypeList>>
            | React.Dispatch<React.SetStateAction<DrawingBooleanTypeList>>;
        ShowEditIconObj:
            | CoverBooleanTypeList
            | StoreOpeningConditionsBooleanTypeList
            | DrawingBooleanTypeList;
        ShowIconsObj:
            | StoreOpeningConditionsBooleanTypeList
            | DrawingBooleanTypeList
            | any;
        handleChatBubbleClick: (labelName: string) => void;
        handleInputBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>, labelName: string) => void;
        handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, labelName: string) => void;
        handleInputClick: (labelName: string) => void;
        handleKeyDown: (e: any, labelName: string) => void;
    };
}

export const LongTextFiledWithIcons: React.FC<TextFieldProps> = ({ props }) => {
    const objLocationBase = useAppSelector(
        (state) => state.longPage.locationBase
    );
    const objRrBranchLog = useAppSelector(
        (state) => state.longPage.rrBranchLog
    );
    const objRequestErrorList = useAppSelector(
        (state) => state.longPage.requestErrorList
    );
    const listDeficiencyComment = useAppSelector(
        (state) => state.deficiencyComment
    );

    // 鉛筆をクリックしたときのフォーカス制御
    const inputRef = useRef<HTMLInputElement | null>(null);

    // 鉛筆をクリックしたとき、Input編集可
    const handleEditIconClick = (labelName: string) => {
        if (
            setPermissionFunc(
                props.userType,
                objLocationBase.task_id,
                props.pageName,
                labelName
            ) === false
        ) {
            //falseにすると、Input編集可
            props.setReadOnlyObj({ ...props.ReadOnlyObj, [labelName]: false });
            
            // iPad対応：z001（RR）かつiPadの場合はフォーカスを当てない
            const isRRUserOnIpad = props.userType === LOCAL_CONSTANT.USER_TYPE.RR && isIpad();
            if (!isRRUserOnIpad) {
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            }
        }
    };

    const changeTextColor = () => {
        let changeColor = false;
        let color = TextDefaultColor;
        changeColor = textColor(
            objRrBranchLog,
            props.pageName,
            props.labelName
        );
        if (changeColor) {
            color = TextChangeColor;
        } else {
            if (!LOCAL_CONSTANT.USER_TYPE.RFC_RR.includes(props.userType)) {
                /* ユーザー種別がRFCとリサーチ以外の場合*/
                color = TextDisabledColor;
            } else if (props.userType === LOCAL_CONSTANT.USER_TYPE.RFC) {
                color = props.disabled ? TextDisabledColor : TextDefaultColor;
            } else if (props.userType === LOCAL_CONSTANT.USER_TYPE.RR) {
                color = props.ReadOnlyObj[props.labelName] ? TextDisabledColor : TextDefaultColor;
            }
        }
        return color;
    };

    const changeBGColor = () => {
        let hasError = false;
        // 入力チェックエラーあり
        if (objRequestErrorList.length > 0) {
            const pageToTableMap = pageToTableMapFunc(props.pageName);
            hasError = objRequestErrorList.some(
                (error: any) =>
                    error.page === setTablePhysicsName(pageToTableMap) &&
                    error.column === props.labelName
            );
            if (hasError) return BGErrorColor;
        }
        // 入力チェックエラーなし
        if (!hasError) {
            // S店(セットバック、立地調査)の出店条件の一部入力欄グレーアウト化
            const targetLocationType = ["06", "07", "08", "09"];
            if(
                (objLocationBase.plan_area_id.startsWith("X") || objLocationBase.plan_area_id.startsWith("Y")) &&
                (targetLocationType.includes(objLocationBase.location_type))
            ) {
                if(props.pageName === "出店条件") {
                    if(
                        props.labelName === "setting_reason" || //ゾーン設定の理由
                        props.labelName === "candidate_location_ruins_long_text" || //候補地跡地 理由
                        props.labelName === "removal_Reason" //店名選定の理由
                    ){
                        return BGDisabledColor;
                    }
                }
            }
            if (props.disabled) {
                return BGDisabledColor;
            } else if (props.InputValueObj[props.labelName].length > 0) {
                return BGChangeColor;
            }
            return BGDefaultColor;
        }
    };

    //ユーザー種別（'z002'、'z004'、'z005'）が選択されている場合のTextFieldのスタイル調整
    const TextFieldSx = () => {
        return {
            "& .MuiFilledInput-root.Mui-disabled": {
                backgroundColor: BGDisabledColor
            },
            "& .MuiOutlinedInput-input": {
                color: TextDisabledColor
            },
            "& .MuiInputBase-root.MuiOutlinedInput-root": {
                backgroundColor: BGDisabledColor,
                paddingTop: props.rows === 1 ? "17px" : "8px",  //ロングテキストが1行の場合はpadding-Topが17px
                paddingBottom: props.rows === 1 ? "17px": "8px" //ロングテキストが1行の場合はpadding-Bottomが17px
            }
        };
    };

    //ユーザー種別（'z003'）が選択されている場合のTextFieldのスタイル調整
    const SelectZ003TextFieldSx = () => {
        const hasBorderColor = changeBorderColor();
        return {
            "& .MuiOutlinedInput-root": {
                "& fieldset": {
                    borderColor: hasBorderColor ? BorderErrorColor : BorderDefaultColor,
                    borderWidth: hasBorderColor ? "3pt" : "1pt"                           //線の幅調整
                },
                "&:hover fieldset": {
                    borderColor: hasBorderColor ? BorderErrorColor : BorderHoverColor,    //ホバー時
                    borderWidth: hasBorderColor ? "3pt" : "1pt"                           //線の幅調整
                },
                "&.Mui-focused fieldset": {
                    borderColor: hasBorderColor ? BorderErrorColor : BorderFocusedColor,  //フォーカス時
                    borderWidth: hasBorderColor ? "3pt" : "2pt"                           //線の幅調整

                }
            },
            "& .MuiOutlinedInput-input": {
                color: changeTextColor(),
            },
            "& .MuiInputBase-root.MuiOutlinedInput-root": {
                backgroundColor: changeBGColor(),
                paddingTop: props.rows === 1 ? "17px" : "8px",  //ロングテキストが1行の場合はpadding-Topが17px
                paddingBottom: props.rows === 1 ? "17px": "8px" //ロングテキストが1行の場合はpadding-Bottomが17px
            },
            cursor: props.ReadOnlyObj[props.labelName] ? "not-allowed" : "text" //鉛筆アイコンをクリック時にTextFieldにカーソルを表示
        };
    };

    //ユーザー種別（'z001'）が選択されている場合のTextFieldのスタイル調整
    const SelectZ001TextFieldSx = () => {
        const hasBorderColor = changeBorderColor();
        return {
            "& .MuiOutlinedInput-root": {
                "& fieldset": {
                    borderColor: hasBorderColor ? BorderErrorColor : BorderDefaultColor,
                    borderWidth: hasBorderColor ? "3pt" : "1pt"                           //線の幅調整
                },
                "&:hover fieldset": {
                    borderColor: hasBorderColor ? BorderErrorColor : BorderHoverColor,    //ホバー時
                    borderWidth: hasBorderColor ? "3pt" : "1pt"                           //線の幅調整

                },
                "&.Mui-focused fieldset": {
                    borderColor: hasBorderColor ? BorderErrorColor : BorderFocusedColor,  //フォーカス時
                    borderWidth: hasBorderColor ? "3pt" : "2pt"                           //線の幅調整
                }
            },
            "& .MuiOutlinedInput-input": {
                color: changeTextColor()
            },
            "& .MuiInputBase-root.MuiOutlinedInput-root": {
                backgroundColor: changeBGColor(),
                paddingTop: props.rows === 1 ? "17px" : "8px",  //ロングテキストが1行の場合はpadding-Topが17px
                paddingBottom: props.rows === 1 ? "17px": "8px" //ロングテキストが1行の場合はpadding-Bottomが17px
            },
            cursor: props.ReadOnlyObj[props.labelName] ? "not-allowed" : "text" //鉛筆アイコンをクリック時にTextFieldにカーソルを表示
        };
    };

    //不備コメントの項目が登録されている場合、borderを赤
    const changeBorderColor = () => {
        if (listDeficiencyComment.length > 0) {
            const pageToTableMap = pageToTableMapFunc(props.pageName);
            return listDeficiencyComment.some(
                (deficiencyComment: any) =>
                    deficiencyComment.table_name === setTablePhysicsName(pageToTableMap) &&
                    deficiencyComment.column_name === props.labelName &&
                    LOCAL_CONSTANT.DEFICIENCY.STATUS.includes(deficiencyComment.deficiency_status)
            );
        } else {
            return false;
        }
    };

    // 吹き出しスタイル
    const chatBubbleErrorSx = () => {
        const hasBorderColor = changeBorderColor();
        let backgroundColor = "";
        if (props.disabled) {
            backgroundColor = hasBorderColor ? BorderErrorColor : "#e0e0e0"; //disabledFlag === true 非活性
        } else {
            backgroundColor = hasBorderColor ? BorderErrorColor : "#1565c0"; //disabledFlag === false 活性
        }
        return {
            backgroundColor: backgroundColor,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "35px",
            height: "35px",
        };
    };

    return (
        <Box>
            {/* ユーザー種別がRFCとリサーチ以外の場合*/}
            {!LOCAL_CONSTANT.USER_TYPE.RFC_RR.includes(props.userType) && (
                <Box sx={{ width: props.width }}>
                    <TextField
                        multiline
                        fullWidth
                        disabled={true}
                        rows={props.rows}
                        value={props.InputValueObj[props.labelName]}
                        sx={TextFieldSx()}
                        variant="outlined"
                    />
                </Box>
            )}
            {/* ユーザー種別がRFCの場合 userTypeがz003*/}
            {props.userType === LOCAL_CONSTANT.USER_TYPE.RFC && (
                <Box
                    display="flex"
                    alignItems="center"
                    sx={{ width: `calc(${props.width} + 90px)` }}
                >
                    <Box sx={{ width: props.width }}>
                        <TextField
                            multiline
                            fullWidth
                            rows={props.rows}
                            value={props.InputValueObj[props.labelName]}
                            onClick={() => props.handleInputClick(props.labelName)}
                            onChange={(e) => props.handleInputChange(e, props.labelName)}
                            onBlur={(e) => props.handleInputBlur(e, props.labelName)}
                            onKeyDown={(e) => props.handleKeyDown(e, props.labelName)}
                            inputRef={inputRef}
                            sx={SelectZ003TextFieldSx()}
                            InputProps={{readOnly: props.disabled}}
                            variant="outlined"
                        />
                    </Box>
                    {/* アイコン全体の表示非表示 */}
                    {props.ShowIconsObj[props.labelName] && (
                        <Box sx={{ minWidth: "90px" }}>
                            {/* 吹き出しアイコン */}
                            <IconButton
                                disabled={props.disabled}
                                onClick={() => props.handleChatBubbleClick(props.labelName)}
                                sx={iconHover}
                            >
                                <Box sx={chatBubbleErrorSx()}>
                                    <ChatBubbleIcon sx={chatBubbleIconSx} />
                                </Box>
                            </IconButton>
                        </Box>
                    )}
                </Box>
            )}
            {/* ユーザー種別がリサーチの場合 userTypeがz001 */}
            {props.userType === LOCAL_CONSTANT.USER_TYPE.RR && (
                <Box
                    display="flex"
                    alignItems="center"
                    sx={{ width: `calc(${props.width} + 90px)` }}
                >
                    <Box sx={{ width: props.width }}>
                        <TextField
                            multiline
                            fullWidth
                            rows={props.rows}
                            value={props.InputValueObj[props.labelName]}
                            onClick={() => props.handleInputClick(props.labelName)}
                            onChange={(e) => props.handleInputChange(e, props.labelName)}
                            onBlur={(e) => props.handleInputBlur(e, props.labelName)}
                            onKeyDown={(e) => props.handleKeyDown(e, props.labelName)}
                            inputRef={inputRef}
                            sx={SelectZ001TextFieldSx()}
                            InputProps={{readOnly: props.ReadOnlyObj[props.labelName]}}
                            variant="outlined"
                        />
                    </Box>
                    {/* アイコン全体の表示非表示 */}
                    {props.ShowIconsObj[props.labelName] && (
                        <Box sx={{ minWidth: "90px" }}>
                            {/* 吹き出しアイコン */}
                            <IconButton
                                disabled={props.disabled}
                                onClick={() => props.handleChatBubbleClick(props.labelName)}
                                sx={iconHover}
                            >
                                <Box sx={chatBubbleErrorSx()}>
                                    <ChatBubbleIcon sx={chatBubbleIconSx} />
                                </Box>
                            </IconButton>
                            {/* 鉛筆アイコンの表示非表示 */}
                            {props.ShowEditIconObj[props.labelName] && (
                                <IconButton
                                    onClick={() => handleEditIconClick(props.labelName)}
                                    sx={iconHover}
                                >
                                    <Box sx={editIconCircleSx}>
                                        <EditIcon />
                                    </Box>
                                </IconButton>
                            )}
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default LongTextFiledWithIcons;
